import React, { useState, useRef, useEffect } from "react";
import {
  RefreshCcw,
  Keyboard,
  Grid,
  Info,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Sparkles,
  Hash,
  Shuffle,
  AlignVerticalJustifyEnd,
} from "lucide-react";
import PatternVisualizer from "./PatternVisualizer";

const BingoGame = () => {
  // Keep essential state variables
  const [currentCombo, setCurrentCombo] = useState("");
  const [calledNumbers, setCalledNumbers] = useState([]);
  const [availableCombos, setAvailableCombos] = useState([]);
  const [isGameComplete, setIsGameComplete] = useState(false);
  const [gameType, setGameType] = useState("75");
  const [isPanelsCollapsed, setIsPanelsCollapsed] = useState(false);
  const [bingoPattern, setBingoPattern] = useState("classic");

  // Remove game mode and related states
  // Keep necessary refs
  const currentComboRef = useRef(currentCombo);
  const luckyIntervalRef = useRef(null);

  // Keep dropdown states
  const [isPatternDropdownOpen, setIsPatternDropdownOpen] = useState(false);
  const patternDropdownRef = useRef(null);
  const [isBallCountDropdownOpen, setIsBallCountDropdownOpen] = useState(false);
  const ballCountDropdownRef = useRef(null);

  // Update current combo ref when it changes
  useEffect(() => {
    currentComboRef.current = currentCombo;
  }, [currentCombo]);

  // Keep existing dropdown handlers and game types
  // ... existing toggle functions for dropdowns ...
  const togglePatternDropdown = () => {
    setIsPatternDropdownOpen((prev) => !prev);
  };

  const toggleBallCountDropdown = () => {
    setIsBallCountDropdownOpen((prev) => !prev);
  };

  const selectPattern = (patternKey) => {
    setBingoPattern(patternKey);
    setIsPatternDropdownOpen(false);
  };

  const selectBallCount = (ballCount) => {
    setGameType(ballCount);
    setIsBallCountDropdownOpen(false);
  };

  // ... existing gameTypes object ...
  const gameTypes = {
    15: {
      name: "15-Ball Bingo",
      description: "Express Mini",
      pattern: (() => {
        const combos = [];
        const numbxcols = 3; // 3 balls per column for 15-ball bingo
        ["B", "I", "N", "G", "O"].forEach((letter, index) => {
          const iStart = index ? index * numbxcols + 1 : 1;
          const iMax = index ? (index + 1) * numbxcols : numbxcols;
          for (let i = iStart; i <= iMax; i++) {
            combos.push(`${letter}-${i}`);
          }
        });
        return combos;
      })(),
    },
    30: {
      // ... keep other game types ...
      name: "30-Ball Bingo",
      description: "Fast & Fun",
      pattern: (() => {
        const combos = [];
        const numbxcols = 6;
        ["B", "I", "N", "G", "O"].forEach((letter, index) => {
          const iStart = index ? index * numbxcols + 1 : 1;
          const iMax = index ? (index + 1) * numbxcols : numbxcols;
          for (let i = iStart; i <= iMax; i++) {
            combos.push(`${letter}-${i}`);
          }
        });
        return combos;
      })(),
    },
    // ... other game types (50, 75, 90) ...
    50: {
      name: "50-Ball Bingo",
      description: "Quick Play",
      pattern: (() => {
        const combos = [];
        const numbxcols = 10;
        ["B", "I", "N", "G", "O"].forEach((letter, index) => {
          const iStart = index ? index * numbxcols + 1 : 1;
          const iMax = index ? (index + 1) * numbxcols : numbxcols;
          for (let i = iStart; i <= iMax; i++) {
            combos.push(`${letter}-${i}`);
          }
        });
        return combos;
      })(),
    },
    75: {
      name: "75-Ball Bingo",
      description: "American Style",
      pattern: (() => {
        const combos = [];
        const numbxcols = 15;
        ["B", "I", "N", "G", "O"].forEach((letter, index) => {
          const iStart = index ? index * numbxcols + 1 : 1;
          const iMax = index ? (index + 1) * numbxcols : numbxcols;
          for (let i = iStart; i <= iMax; i++) {
            combos.push(`${letter}-${i}`);
          }
        });
        return combos;
      })(),
    },
    90: {
      name: "90-Ball Bingo",
      description: "British Style",
      pattern: (() => {
        const combos = [];
        const numbxcols = 18;
        ["B", "I", "N", "G", "O"].forEach((letter, index) => {
          const iStart = index ? index * numbxcols + 1 : 1;
          const iMax = index ? (index + 1) * numbxcols : numbxcols;
          for (let i = iStart; i <= iMax; i++) {
            combos.push(`${letter}-${i}`);
          }
        });
        return combos;
      })(),
    },
  };

  // Initialize game (simplified)
  const initializeGame = (selectedGameType = gameType) => {
    const combos = [...gameTypes[selectedGameType].pattern];
    setAvailableCombos(combos);
    setCalledNumbers([]);
    setIsGameComplete(false);
    setCurrentCombo(combos[0]);
    setIsPanelsCollapsed(true);
  };

  // Initialize game on mount and when game type changes
  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    initializeGame(gameType);
  }, [gameType]);

  // Generate a random combination from available numbers (keep this)
  const generateCombo = () => {
    if (availableCombos.length === 0) return null;
    const index = Math.floor(Math.random() * availableCombos.length);
    return availableCombos[index];
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      clearInterval(luckyIntervalRef.current);
    };
  }, []);

  // Simplify to just the Lucky Button handler (renamed to randomCall)
  const randomCall = () => {
    if (
      luckyIntervalRef.current ||
      availableCombos.length === 0 ||
      isGameComplete
    )
      return;

    // Start rapid generation
    luckyIntervalRef.current = setInterval(() => {
      const newCombo = generateCombo();
      if (newCombo) {
        setCurrentCombo(newCombo);
      }
    }, 20);

    // Stop after 1 second
    setTimeout(() => {
      clearInterval(luckyIntervalRef.current);
      luckyIntervalRef.current = null;

      const finalCombo = currentComboRef.current;
      if (finalCombo && !calledNumbers.includes(finalCombo)) {
        setCalledNumbers((prev) => [finalCombo, ...prev]);
        setAvailableCombos((prev) =>
          prev.filter((combo) => combo !== finalCombo)
        );
        setCurrentCombo(finalCombo);

        // Check if this was the last number
        if (availableCombos.length === 1) {
          setIsGameComplete(true);
        }
      }
    }, 1000);
  };

  // Toggle panels visibility
  const togglePanels = () => {
    setIsPanelsCollapsed((prev) => !prev);
  };

  // Generate bingo card based on game type
  const generateBingoCard = () => {
    // Check if the current game type exists, if not, default to 75
    const currentGameType = gameTypes[gameType] ? gameType : "75";

    const combos = gameTypes[currentGameType].pattern;
    const card = {
      B: [],
      I: [],
      N: [],
      G: [],
      O: [],
    };

    combos.forEach((combo) => {
      const letter = combo.charAt(0);
      if (card[letter]) {
        card[letter].push({
          label: combo,
          called: calledNumbers.includes(combo),
        });
      }
    });
    return card;
  };

  const bingoCard = generateBingoCard();

  // Pattern types - EXPANDED with 20 new patterns
  const patternTypes = {
    // Existing patterns
    classic: {
      name: "Classic Bingo",
      description: "Complete any row, column, or diagonal",
      icon: "🎯",
    },
    fourCorners: {
      name: "Four Corners",
      description: "Mark the four corners of the card",
      icon: "◢◣◥◤",
    },
    letterX: {
      name: "Letter X",
      description: "Mark both diagonals to form an X",
      icon: "❌",
    },
    letterL: {
      name: "Letter L",
      description: "Complete the leftmost column and bottom row",
      icon: "⅃",
    },
    letterT: {
      name: "Letter T",
      description: "Complete the top row and middle column",
      icon: "T",
    },
    postage: {
      name: "Postage Stamp",
      description: "Mark a 2×2 square in any corner",
      icon: "🎫",
    },
    letterH: {
      name: "Letter H",
      description: "Complete both outer columns and middle row",
      icon: "H",
    },
    letterZ: {
      name: "Letter Z",
      description: "Top row, diagonal, and bottom row",
      icon: "Z",
    },
    letterN: {
      name: "Letter N",
      description: "Left column, diagonal, and right column",
      icon: "N",
    },
    plus: {
      name: "Plus Sign",
      description: "Complete middle row and middle column",
      icon: "➕",
    },
    diamond: {
      name: "Diamond",
      description: "Form a diamond pattern in the center",
      icon: "♦️",
    },
    centerSquare: {
      name: "Center Square",
      description: "Complete the 3×3 center square",
      icon: "⬚",
    },
    corners2x2: {
      name: "Four 2×2 Corners",
      description: "Complete 2×2 squares in all four corners",
      icon: "◱◲◳◰",
    },
    frame: {
      name: "Picture Frame",
      description: "Complete the outside frame",
      icon: "🖼️",
    },
    checkmark: {
      name: "Checkmark",
      description: "Diagonal from bottom-left to middle, then right",
      icon: "✓",
    },
    blackout: {
      name: "Blackout",
      description: "Mark all numbers on the card",
      icon: "⬛",
    },

    // NEW PATTERNS - ADDING 20 MORE PATTERNS
    // Geometric Patterns
    pyramid: {
      name: "Pyramid",
      description: "Form a pyramid shape from bottom to top",
      icon: "🔺",
    },
    hourglass: {
      name: "Hourglass",
      description: "Complete the hourglass (or bow tie) shape",
      icon: "⌛",
    },
    arrow: {
      name: "Arrow",
      description: "Form an arrow pointing up",
      icon: "⬆️",
    },
    cross: {
      name: "Cross",
      description: "Complete both middle row and column (larger plus)",
      icon: "✚",
    },
    doubleX: {
      name: "Double X",
      description: "X pattern plus the four corners",
      icon: "✶",
    },

    // More Letter Patterns
    letterU: {
      name: "Letter U",
      description: "Complete both outer columns and bottom row",
      icon: "U",
    },
    letterO: {
      name: "Letter O",
      description: "Complete the outer frame without corners",
      icon: "O",
    },
    letterY: {
      name: "Letter Y",
      description: "Form a Y shape from top center",
      icon: "Y",
    },
    letterE: {
      name: "Letter E",
      description: "Left column with top, middle, and bottom rows",
      icon: "E",
    },
    letterF: {
      name: "Letter F",
      description: "Left column with top and middle rows",
      icon: "F",
    },

    // Special Shapes
    heart: {
      name: "Heart Shape",
      description: "Form a heart-like shape on the card",
      icon: "❤️",
    },
    target: {
      name: "Target",
      description: "Center spot plus the surrounding 8 spots",
      icon: "🎯",
    },
    windmill: {
      name: "Windmill",
      description: "Center plus lines extending to each corner",
      icon: "💨",
    },
    kite: {
      name: "Kite",
      description: "Form a kite shape from center to edges",
      icon: "🪁",
    },
    stamp: {
      name: "Stamp",
      description: "Complete a postage stamp in each corner",
      icon: "📬",
    },

    // Game-Themed Patterns
    dice: {
      name: "Lucky Six",
      description: "Spots arranged like the six on a die",
      icon: "🎲",
    },
    crown: {
      name: "Crown",
      description: "Top row with peaks descending into the card",
      icon: "👑",
    },
    star: {
      name: "Star Pattern",
      description: "Form a star shape centered on the card",
      icon: "⭐",
    },
    rainbowArch: {
      name: "Rainbow Arch",
      description: "Complete a rainbow-shaped arch at the top",
      icon: "🌈",
    },
    cup: {
      name: "Cup",
      description: "Complete a cup or trophy shape",
      icon: "🏆",
    },
  };

  // Ball counts configuration
  const ballCounts = {
    // ... keep existing ball counts ...
    15: { name: "15-Ball Bingo", description: "Express Mini", icon: "🏎️" },
    30: { name: "30-Ball Bingo", description: "Fast & Fun", icon: "⚡" },
    50: { name: "50-Ball Bingo", description: "Quick Play", icon: "🔄" },
    75: { name: "75-Ball Bingo", description: "American Style", icon: "🇺🇸" },
    90: { name: "90-Ball Bingo", description: "British Style", icon: "🇬🇧" },
  };

  return (
    <div className="bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 bg-cover bg-center p-4 sm:p-8 animate-gradient">
      <div className="max-w-7xl mx-auto">
        {/* Header and Game Type Selection - keep as is */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center relative space-x-6">
            <h1 className="text-6xl font-extrabold text-center text-white bg-black bg-opacity-50 rounded-lg p-4 drop-shadow-3xl flex-grow">
              🎉✨ Call My Bingo! 🎈🎊
            </h1>

            <button
              onClick={() => initializeGame()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-full font-bold hover:from-green-500 hover:to-blue-600 transition-all duration-300 ease-in-out shadow-lg transform hover:scale-105"
              title="Start a New Game"
            >
              <RefreshCcw size={20} /> New Game
            </button>

            <button
              onClick={togglePanels}
              className="ml-4 flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-full font-bold hover:bg-purple-700 transition-transform transform hover:scale-110 z-20"
              title={isPanelsCollapsed ? "Reveal Panels" : "Hide Panels"}
            >
              {isPanelsCollapsed ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronUp size={20} />
              )}
              {isPanelsCollapsed ? "Reveal Panels" : "Hide Panels"}
            </button>
          </div>

          {/* Game Type Selector - keep as is */}
          <div
            className={`relative transition-all duration-500 ease-in-out ${
              isPanelsCollapsed
                ? "max-h-0 opacity-0 overflow-hidden"
                : "max-h-screen opacity-100"
            }`}
          >
            {!isPanelsCollapsed && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(gameTypes).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => setGameType(type)}
                    className={`p-4 rounded-xl transition-all transform hover:scale-105 ${
                      gameType === type
                        ? "bg-purple-500 text-white shadow-lg scale-105"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 justify-center mb-1">
                      <Grid size={16} />
                      <span className="font-bold">{config.name}</span>
                    </div>
                    <div className="text-sm opacity-75">
                      {config.description}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isGameComplete ? (
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <div className="text-6xl font-bold text-purple-600 mb-4 animate-bounce">
              BINGO! 🎉
            </div>
            <p className="text-2xl text-gray-600 mb-4">
              All numbers have been called!
            </p>
            <button
              onClick={initializeGame}
              className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-colors mx-auto"
            >
              <RefreshCcw size={24} /> Start New Game
            </button>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 mt-4">
            {/* Column 1: SIMPLIFIED Controls and Generated Numbers */}
            <div className="flex-1 flex flex-col gap-4">
              {/* Simplified Game Controls */}
              <div className="bg-gradient-to-tr from-purple-600 to-indigo-700 rounded-xl p-5 shadow-2xl relative flex items-center justify-center">
                {/* Info tooltip */}
                <div className="absolute top-3 right-3 group z-10">
                  <Info
                    className="text-white hover:text-yellow-300 cursor-pointer"
                    size={18}
                  />
                  <div className="absolute -top-20 right-0 w-56 bg-gray-800 text-white text-xs rounded-md p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 pointer-events-none">
                    Click the Random Call button to draw a bingo number!
                  </div>
                </div>

                {/* Just the Random Call button */}
                <button
                  onClick={randomCall}
                  disabled={availableCombos.length === 0 || isGameComplete}
                  className="flex items-center gap-2 px-8 py-4 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-white rounded-lg font-bold text-xl transition-colors transform hover:scale-105 shadow-lg"
                >
                  <Shuffle size={24} />
                  Random Call!
                </button>

                {/* Numbers remaining counter */}
                <div className="absolute bottom-3 right-3 text-white text-sm font-medium bg-purple-800 bg-opacity-50 px-3 py-1.5 rounded-lg">
                  Numbers Left: <strong>{availableCombos.length}</strong>
                </div>
              </div>

              {/* Generated Number Display Panel - same as before but taller */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-2xl text-center flex-1 flex flex-col items-center justify-center py-16 animate-glow">
                <div
                  className={`text-7xl md:text-8xl lg:text-9xl font-extrabold text-white animate-pulseSlow mb-4 ${
                    !currentCombo && "opacity-50"
                  }`}
                >
                  {currentCombo || "Ready!"}
                </div>
                {calledNumbers.length > 0 && (
                  <div className="text-white text-opacity-90 text-xl mt-2">
                    Last called:
                    <span className="ml-2 text-yellow-300 font-bold">
                      {calledNumbers[0]}
                    </span>
                  </div>
                )}
                <div className="text-white text-xs opacity-75 absolute bottom-4">
                  <Keyboard size={12} className="inline mr-1" />
                  Click the Random Call button to draw a number
                </div>
              </div>
            </div>

            {/* Column 2: RESTRUCTURED - Bingo Card + Pattern + Called Numbers */}
            <div className="flex-1 flex flex-col gap-4">
              {/* Bingo Card Panel - MADE SMALLER */}
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-3 shadow-lg">
                <div className="flex justify-between items-center mb-3">
                  {/* Ball Count Dropdown - Keep unchanged */}
                  <div className="relative" ref={ballCountDropdownRef}>
                    <button
                      onClick={toggleBallCountDropdown}
                      className="pattern-dropdown-trigger px-4 py-2 flex items-center gap-2 rounded-lg text-white"
                    >
                      <span className="pattern-option-icon">
                        {ballCounts[gameType].icon}
                      </span>
                      <span className="font-medium">
                        {ballCounts[gameType].name}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${
                          isBallCountDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Ball Count Dropdown Menu */}
                    <div
                      className={`absolute left-0 mt-3 z-50 pattern-dropdown-content transition-all duration-300 ball-count-dropdown ${
                        isBallCountDropdownOpen
                          ? "opacity-100 visible scale-100"
                          : "opacity-0 invisible scale-95"
                      }`}
                    >
                      <div className="pattern-dropdown-header bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                        <div className="flex items-center justify-center gap-2">
                          <Hash size={16} className="text-yellow-300" />
                          <span>Select Ball Count</span>
                          <Hash size={16} className="text-yellow-300" />
                        </div>
                      </div>

                      <div className="pattern-grid-container ball-count-grid bg-white shadow-inner">
                        {Object.entries(ballCounts).map(([count, config]) => (
                          <button
                            key={count}
                            onClick={() => selectBallCount(count)}
                            className={`pattern-option ${
                              gameType === count
                                ? "selected bg-purple-50"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <span className="pattern-option-icon">
                              {config.icon}
                            </span>
                            <div className="pattern-option-text">
                              <div className="font-medium text-gray-800 text-sm">
                                {config.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {config.description}
                              </div>
                            </div>
                            {gameType === count && (
                              <CheckCircle
                                size={16}
                                className="pattern-option-selected-icon"
                              />
                            )}
                          </button>
                        ))}
                      </div>

                      <div className="pattern-dropdown-footer bg-gray-50 text-gray-600 border-t border-gray-200">
                        Choose the type of bingo game to play
                      </div>
                    </div>
                  </div>

                  {/* Pattern Type Dropdown */}
                  <div
                    className="relative pattern-dropdown"
                    ref={patternDropdownRef}
                  >
                    <button
                      onClick={togglePatternDropdown}
                      className="pattern-dropdown-trigger px-4 py-2 flex items-center gap-2 rounded-lg text-white"
                    >
                      <span className="pattern-option-icon">
                        {patternTypes[bingoPattern].icon}
                      </span>
                      <span className="hidden sm:inline font-medium">
                        {patternTypes[bingoPattern].name}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-300 ${
                          isPatternDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Pattern Dropdown Menu - MODIFIED to use 4 columns */}
                    <div
                      className={`absolute right-0 mt-3 z-50 pattern-dropdown-content transition-all duration-300 ${
                        isPatternDropdownOpen
                          ? "opacity-100 visible scale-100"
                          : "opacity-0 invisible scale-95"
                      }`}
                    >
                      <div className="pattern-dropdown-header bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles size={16} className="text-yellow-300" />
                          <span>Select Winning Pattern</span>
                          <Sparkles size={16} className="text-yellow-300" />
                        </div>
                      </div>

                      {/* Pattern grid container - MODIFIED to use 4 columns */}
                      <div className="pattern-grid-container bg-white max-h-[500px] overflow-y-auto shadow-inner p-1">
                        {Object.entries(patternTypes).map(([key, pattern]) => (
                          <button
                            key={key}
                            onClick={() => selectPattern(key)}
                            className={`pattern-option ${
                              bingoPattern === key
                                ? "selected bg-purple-50"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <span className="pattern-option-icon">
                              {pattern.icon}
                            </span>
                            <div className="pattern-option-text">
                              <div className="font-medium text-gray-800 text-sm">
                                {pattern.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {pattern.description}
                              </div>
                            </div>
                            {bingoPattern === key && (
                              <CheckCircle
                                size={16}
                                className="pattern-option-selected-icon text-purple-600"
                              />
                            )}
                          </button>
                        ))}
                      </div>

                      <div className="pattern-dropdown-footer bg-gray-50 text-gray-600 border-t border-gray-200">
                        Choose how players will win this game
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bingo Card Grid - MADE SMALLER */}
                <div className="border border-white rounded-lg overflow-hidden">
                  {/* Bingo Card Headers */}
                  <div className="grid grid-cols-5 bg-purple-700 text-white">
                    {["B", "I", "N", "G", "O"].map((letter) => (
                      <div
                        key={letter}
                        className="p-1 text-sm border-r border-b border-white text-center font-semibold uppercase"
                      >
                        {letter}
                      </div>
                    ))}
                  </div>

                  {/* Bingo Card Numbers - MADE SMALLER */}
                  <div className="grid grid-cols-5">
                    {["B", "I", "N", "G", "O"].map((letter) => (
                      <div key={letter} className="flex flex-col">
                        {bingoCard[letter].map((cell, index) => (
                          <div
                            key={index}
                            className={`p-1 text-sm border-r border-b border-white flex items-center justify-center 
                              ${
                                cell.called
                                  ? "bg-white text-purple-700 font-bold rounded-full animate-pulse"
                                  : "bg-transparent text-white"
                              }`}
                            title={cell.called ? "Called" : "Available"}
                          >
                            {cell.label}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* NEW LAYOUT: Two-column layout for Pattern and Called Numbers */}
              <div className="grid grid-cols-2 gap-4">
                {/* Current Pattern Panel - MOVED HERE */}
                <div className="bg-white bg-opacity-90 rounded-xl p-3 shadow-lg">
                  <h3 className="font-bold text-purple-800 text-lg mb-2 flex items-center">
                    <Sparkles size={18} className="mr-2 text-purple-500" />
                    Current Pattern
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-purple-100 to-pink-50 p-2 rounded-lg">
                      <PatternVisualizer patternType={bingoPattern} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-1">
                        <span className="text-lg">
                          {patternTypes[bingoPattern].icon}
                        </span>
                        <span>{patternTypes[bingoPattern].name}</span>
                      </div>
                      <p className="text-xs text-gray-600 bg-purple-50 p-1 mt-1 rounded border-l-2 border-purple-300">
                        {patternTypes[bingoPattern].description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Called Numbers Panel - MOVED HERE */}
                <div className="bg-white bg-opacity-90 rounded-xl p-3 shadow-lg">
                  <h3 className="font-bold text-purple-800 text-lg mb-2 flex items-center">
                    <AlignVerticalJustifyEnd
                      size={18}
                      className="mr-2 text-purple-500"
                    />
                    Called Numbers{" "}
                    <span className="text-sm ml-1 text-purple-500">
                      ({calledNumbers.length})
                    </span>
                  </h3>
                  <div className="flex flex-wrap gap-1 max-h-[110px] overflow-y-auto">
                    {calledNumbers.map((num, index) => (
                      <div
                        key={index}
                        className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm font-medium"
                      >
                        {num}
                      </div>
                    ))}
                    {calledNumbers.length === 0 && (
                      <p className="text-gray-500 text-xs italic">
                        No numbers have been called yet. Click "Random Call" to
                        begin.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Game Instructions */}
              <div className="bg-indigo-800 bg-opacity-40 rounded-xl p-3 shadow-lg text-center">
                <p className="text-white text-sm">
                  <span className="font-semibold">How To Play:</span> Click
                  "Random Call" to draw numbers. Players mark matching numbers
                  on their cards. Winner completes the pattern above!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add global CSS for dropdown styling */}
      <style jsx global>{`
        .pattern-dropdown-content {
          width: 640px; /* Increased width to accommodate 4 columns */
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.2);
          max-width: 92vw; /* Ensure it doesn't overflow on small screens */
        }

        .ball-count-dropdown {
          width: 280px;
        }

        .pattern-dropdown-header {
          padding: 0.75rem 1rem;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .pattern-grid-container {
          display: grid;
          grid-template-columns: repeat(4, 1fr); /* Changed to 4 columns */
          gap: 0.5rem;
          padding: 0.75rem;
        }

        @media (max-width: 768px) {
          .pattern-grid-container {
            grid-template-columns: repeat(
              2,
              1fr
            ); /* Fall back to 2 columns on mobile */
          }
        }

        .ball-count-grid {
          grid-template-columns: 1fr;
        }

        /* Made pattern options more compact to fit in 4-column layout */
        .pattern-option {
          display: flex;
          align-items: center;
          padding: 0.5rem;
          border-radius: 0.375rem;
          transition: all 0.15s ease;
          position: relative;
          border: 1px solid transparent;
        }

        .pattern-option.selected {
          border-color: #8b5cf6;
        }

        .pattern-option-icon {
          font-size: 1.25rem;
          margin-right: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 1.5rem;
        }

        .pattern-option-text {
          flex: 1;
          min-width: 0; /* Allow text to shrink */
        }

        .pattern-option-selected-icon {
          position: absolute;
          right: 0.5rem;
        }

        .pattern-dropdown-footer {
          padding: 0.5rem 1rem;
          font-size: 0.75rem;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default BingoGame;
